import React, { ReactNode } from 'react';
import { flexRender, HeaderGroup, Row } from '@tanstack/react-table';
import styled, { css } from 'styled-components';
import TableHeading from './TableHeading';
import { theme } from '../../styles/theme';
import { getScrollBarStyles } from '../../config/styles';
import { level1Content } from '../../styles/shareableStyles';

interface IProps<TData> {
  headerGroups: HeaderGroup<TData>[];
  rows: Row<TData>[];
  noContentComponent?: ReactNode;
  className?: string;
  getIsDisabled?: (row: TData) => boolean | undefined;
}

const ScrollableContainer = styled.div`
  overflow-x: auto;
  ${getScrollBarStyles(4)};
`;

const Container = styled.div`
  width: 100%;
  min-width: fit-content;
  font-size: 12px;
  ${level1Content};
`;
const TableRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: 6px 24px;
  column-gap: 24px;
  text-align: left;
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border};
  }
  &:last-child {
    border-bottom-right-radius: 3px;
    border-bottom-left-radius: 3px;
  }
`;
const TableHeaderRow = styled(TableRow)`
  min-height: 48px;
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  background-color: ${theme.colors.tableHeader};
`;
const TableHeaderCell = styled.div`
  flex: 0 0 auto;
`;
const TableBodyRow = styled(TableRow)<{ $isDisabled?: boolean }>`
  min-height: 48px;
  color: ${theme.colors.secondary800};
  &:nth-of-type(even) {
    background-color: ${theme.colors.level2};
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${theme.colors.secondary400};
    `}
`;
const TableCell = styled.div`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
`;

const Table = <TData,>({
  headerGroups,
  rows,
  noContentComponent,
  className,
  getIsDisabled,
}: IProps<TData>) => {
  return (
    <ScrollableContainer className={className}>
      <Container>
        {headerGroups.map((headerGroup) => (
          <TableHeaderRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              if (header.column.columnDef.enableSorting) {
                return (
                  <TableHeaderCell
                    key={header.id}
                    style={header?.column?.columnDef?.meta?.style}
                  >
                    <TableHeading
                      alignment={header?.column?.columnDef?.meta?.alignment}
                      sortDirection={header.column.getIsSorted()}
                      onClick={() =>
                        header.column.toggleSorting(
                          header.column.getIsSorted() === 'asc',
                        )
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHeading>
                  </TableHeaderCell>
                );
              }

              return (
                <TableHeaderCell
                  key={header.id}
                  style={header?.column?.columnDef?.meta?.style}
                >
                  <TableHeading>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHeading>
                </TableHeaderCell>
              );
            })}
          </TableHeaderRow>
        ))}
        {rows.length > 0 ? (
          <>
            {rows.map((row) => (
              <TableBodyRow
                key={row.id}
                $isDisabled={getIsDisabled?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={cell?.column?.columnDef?.meta?.style}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableBodyRow>
            ))}
          </>
        ) : (
          noContentComponent
        )}
      </Container>
    </ScrollableContainer>
  );
};

export default Table;
